/**
 * Free Gift With Purchase
 * Adds the gift product to the cart once the cart holds enough qualifying products,
 * and removes it again when it no longer qualifies. Config comes from the
 * #gift-with-purchase-config JSON rendered in header-actions.liquid.
 * The gift is charged at its normal price unless a Shopify discount makes it free.
 */
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'gift-with-purchase';
let isSyncing = false;
let syncAgain = false;
let syncTimer = null;

function getConfig() {
  const el = document.getElementById('gift-with-purchase-config');
  if (!el) return null;
  try {
    const config = JSON.parse(el.textContent);
    if (!config.giftVariantId) return null;
    if (!config.anyProduct && !config.qualifyingProductIds?.length) return null;
    return config;
  } catch {
    return null;
  }
}

function jsUrl(route, fallback) {
  const url = String(route ?? fallback).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

function refreshSections(sections, sectionIds) {
  if (sections && Object.keys(sections).length > 0) {
    sectionIds.forEach((id) => sections[id] && morphSection(id, sections[id]));
  } else {
    sectionIds.forEach((id) => sectionRenderer.renderSection(id, { cache: false }));
  }
}

async function addGift(config, sectionIds) {
  const formData = new FormData();
  formData.append('id', String(config.giftVariantId));
  formData.append('quantity', '1');
  if (sectionIds.length > 0) {
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  const response = await fetch(jsUrl(window.Theme?.routes?.cart_add_url, '/cart/add.js'), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (data.status || !response.ok) throw new Error(data.message || 'Failed to add free gift');

  refreshSections(data.sections, sectionIds);
  document.dispatchEvent(
    new CartAddEvent({}, SOURCE, {
      source: SOURCE,
      itemCount: 1,
      variantId: String(config.giftVariantId),
      sections: data.sections,
    })
  );
}

async function setGiftQuantity(lineKey, quantity, sectionIds) {
  const body = JSON.stringify({
    id: lineKey,
    quantity,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(
    jsUrl(window.Theme?.routes?.cart_change_url, '/cart/change.js'),
    fetchConfig('json', { body })
  );
  const data = await response.json().catch(() => ({}));
  if (data.errors || !response.ok) throw new Error(data.errors || 'Failed to update free gift');

  refreshSections(data.sections, sectionIds);
  document.dispatchEvent(
    new CartUpdateEvent(data, sectionIds[0], {
      source: SOURCE,
      itemCount: data.item_count ?? 0,
      sections: data.sections,
    })
  );
}

async function syncGift() {
  const config = getConfig();
  if (!config) return;

  if (isSyncing) {
    syncAgain = true;
    return;
  }
  isSyncing = true;

  try {
    const root = window.Shopify?.routes?.root || '/';
    const cartResponse = await fetch(`${root.endsWith('/') ? root : `${root}/`}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    const cart = await cartResponse.json();
    const items = cart.items || [];

    const giftVariantId = String(config.giftVariantId);
    const qualifyingIds = new Set((config.qualifyingProductIds || []).map(String));
    // Add-ons such as the gift note never count towards the gift
    const excludedIds = new Set((config.excludedVariantIds || []).map(String));
    const qualifyingQuantity = items
      .filter((item) => {
        const variantId = String(item.variant_id);
        if (variantId === giftVariantId || excludedIds.has(variantId)) return false;
        return config.anyProduct || qualifyingIds.has(String(item.product_id));
      })
      .reduce((sum, item) => sum + item.quantity, 0);

    const giftLines = items.filter((item) => String(item.variant_id) === giftVariantId);
    const giftQuantity = giftLines.reduce((sum, item) => sum + item.quantity, 0);
    const qualifies = qualifyingQuantity >= (config.minQuantity || 1);
    const sectionIds = getCartSectionIds();

    if (qualifies && giftQuantity === 0) {
      await addGift(config, sectionIds);
    } else if (!qualifies && giftQuantity > 0) {
      for (const line of giftLines) await setGiftQuantity(line.key, 0, sectionIds);
    } else if (qualifies && giftQuantity > 1) {
      // Keep exactly one free gift
      await setGiftQuantity(giftLines[0].key, 1, sectionIds);
      for (const line of giftLines.slice(1)) await setGiftQuantity(line.key, 0, sectionIds);
    }
  } catch (err) {
    console.error('Free gift with purchase error:', err);
  } finally {
    isSyncing = false;
    if (syncAgain) {
      syncAgain = false;
      scheduleSync();
    }
  }
}

function scheduleSync() {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(syncGift, 150);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SOURCE) return;
  scheduleSync();
});

scheduleSync();
